'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { nhost } from '@/lib/nhost'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'

export function AffiliateLoginForm() {
  const t = useTranslations('booking.affiliate.login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email || !password) {
      setError(t('enterCredentials'))
      return
    }

    setLoading(true)
    try {
      // Nhost v4: failures throw FetchError (handled by the catch below); the response
      // object carries no `.error` field. NOTE: the previous `.error` branch with
      // invalidCredentials/accountDisabled messages was dead code since the v4 migration.
      await nhost.auth.signInEmailPassword({ email, password })

      // Success — full navigation so agency portal AuthProvider picks up session.
      // Deliberately NOT routed through `usePortalHref`: this form is served on a
      // BOOKING host (tenant.book.taskyinn.sa / a custom domain), never on
      // agency.taskyinn.sa, so the canonical `/agency` path is the address there
      // and there is no PortalHostProvider above this tree to ask.
      window.location.href = '/agency'
    } catch {
      setError(t('somethingWrong'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email">{t('emailLabel')}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('placeholders.email')}
          autoComplete="email"
          disabled={loading}
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">{t('passwordLabel')}</Label>
          <Link href="/reset-password"
            className="text-xs text-primary hover:underline"
          >
            {t('forgotPassword')}
          </Link>
        </div>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('placeholders.password')}
            autoComplete="current-password"
            disabled={loading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        {t('signIn')}
      </Button>
    </form>
  )
}
